import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/client';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Volume2,
  Flag,
  X,
  MessageSquare,
  Shuffle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { speakText as speakWithBestVoice } from '../utils/speech';
import motivationQuoteData from '../data/motivation_quotes.json';

const StudySession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [dailyReviewed, setDailyReviewed] = useState(0);
  const [dailyGoal, setDailyGoal] = useState(50);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [direction, setDirection] = useState(0);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagComment, setFlagComment] = useState('');
  const [flagging, setFlagging] = useState(false);
  const [shuffle, setShuffle] = useState(true);
  const difficultOnly = Boolean(location.state?.difficultOnly);
  const [selectedTopic, setSelectedTopic] = useState(
    () => location.state?.topic ?? localStorage.getItem('studyTopic') ?? ''
  );

  const [dictionaryType, setDictionaryType] = useState(
    location.state?.dictionaryType || localStorage.getItem('dictionaryType') || 'hsk'
  );
  const hskLevel = dictionaryType === 'hsk' ? (location.state?.hskLevel || null) : null;
  const dailyReviewLimit = Number(
    location.state?.dailyReviewLimit || localStorage.getItem('dailyReviewLimit') || 50
  );

  const currentCard = session?.cards[currentIndex];
  const motivationQuotes = motivationQuoteData.dictionaries[dictionaryType]?.quotes || [];
  const motivationQuote = motivationQuotes[currentIndex % motivationQuotes.length];
  const isCurrentCompleted = currentCard ? completed.includes(currentCard.id) : false;
  const sessionCardCount = session?.cards?.length || 0;
  const progress = sessionCardCount ? (completed.length / sessionCardCount) * 100 : 0;

  useEffect(() => {
    loadSession();
  }, [hskLevel, dictionaryType, selectedTopic, shuffle, difficultOnly]);

  const loadSession = async () => {
    try {
      const params = {
        shuffle,
        limit: dailyReviewLimit,
        dictionary_type: dictionaryType,
        difficult_only: difficultOnly
      };
      if (hskLevel) params.hsk_level = hskLevel;
      if (selectedTopic) params.topic = selectedTopic;
      const [sessionResponse, dailyResponse] = await Promise.all([
        api.get('/api/cards/study/session', { params }),
        api.get('/api/stats/daily')
      ]);
      setSession(sessionResponse.data);
      setDailyReviewed(dailyResponse.data.reviewed_today || 0);
      setDailyGoal(dailyResponse.data.daily_goal || dailyReviewLimit);
      if (sessionResponse.data.total_count === 0) {
        toast.success('No cards due for review! Great job! 🎉');
      }
    } catch (error) {
      toast.error('Failed to load study session');
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = useCallback(async () => {
    if (!currentCard || flagging) return;

    setFlagging(true);
    try {
      const newFlagged = !currentCard.flagged;
      await api.post(`/api/cards/${currentCard.id}/flag`, {
        flagged: newFlagged,
        user_comment: newFlagged ? flagComment : currentCard.user_comment
      });

      // Update the card in the session
      const updatedCard = { ...currentCard, flagged: newFlagged, user_comment: newFlagged ? flagComment : null };
      const updatedCards = session.cards.map(c => c.id === currentCard.id ? updatedCard : c);
      setSession({ ...session, cards: updatedCards });

      setShowFlagModal(false);
      setFlagComment('');
      toast.success(newFlagged ? '🚩 Card flagged for review' : '✅ Flag removed');
    } catch (error) {
      toast.error('Failed to update flag');
    } finally {
      setFlagging(false);
    }
  }, [currentCard, flagComment, flagging, session]);

  const handleUpdateFlag = useCallback(async () => {
    if (!currentCard || flagging) return;

    setFlagging(true);
    try {
      await api.post(`/api/cards/${currentCard.id}/flag`, {
        flagged: true,
        user_comment: flagComment
      });

      const updatedCard = { ...currentCard, user_comment: flagComment };
      const updatedCards = session.cards.map(c => c.id === currentCard.id ? updatedCard : c);
      setSession({ ...session, cards: updatedCards });

      setShowFlagModal(false);
      setFlagComment('');
      toast.success('✅ Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    } finally {
      setFlagging(false);
    }
  }, [currentCard, flagComment, flagging, session]);

  const handleReview = useCallback(async (qualityRating) => {
    if (!currentCard || !revealed || reviewing) return;

    setReviewing(true);
    try {
      await api.post('/api/cards/review', {
        card_id: currentCard.id,
        quality_rating: qualityRating
      });

      setDailyReviewed((count) => count + 1);
      setCompleted([...completed, currentCard.id]);

      if (currentIndex < (session?.cards.length || 0) - 1) {
        setDirection(1);
        setCurrentIndex(currentIndex + 1);
        setRevealed(false);
      } else {
        toast.success('Study session complete! 🎉');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to save review');
    } finally {
      setReviewing(false);
    }
  }, [currentCard, currentIndex, completed, session, navigate, reviewing, revealed]);

  const handlePrevious = () => {
    if (currentIndex === 0 || reviewing) return;
    setDirection(-1);
    setCurrentIndex(currentIndex - 1);
    setRevealed(false);
  };

  const handleNext = () => {
    if (!session || currentIndex >= session.cards.length - 1 || reviewing) return;
    setDirection(1);
    setCurrentIndex(currentIndex + 1);
    setRevealed(false);
  };

  const handleKeyDown = useCallback((e) => {
    if (!currentCard || reviewing) return;

    // Don't intercept keys when typing in input/textarea
    const tag = document.activeElement?.tagName.toLowerCase();
    const isTyping = tag === 'input' || tag === 'textarea';
    if (isTyping) return;

    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        if (!revealed) {
          setRevealed(true);
          speakCard(currentCard.word);
        } else {
          setRevealed(false);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (revealed) handleReview(0); // Again
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (revealed) handleReview(2); // Good
        break;
      case '1':
        if (!revealed) break;
        e.preventDefault();
        handleReview(0); // Again
        break;
      case '2':
        if (!revealed) break;
        e.preventDefault();
        handleReview(1); // Hard
        break;
      case '3':
        if (!revealed) break;
        e.preventDefault();
        handleReview(2); // Good
        break;
      case '4':
        if (!revealed) break;
        e.preventDefault();
        handleReview(3); // Easy
        break;
      default:
        break;
    }
  }, [currentCard, revealed, handleReview, reviewing]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const speakText = (text, lang = 'zh-CN') => speakWithBestVoice(text, lang);

  const speakCard = (text) => speakText(text, dictionaryType === 'ielts' ? 'en-US' : 'zh-CN');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading your cards...</p>
        </div>
      </div>
    );
  }

  if (!session || session.total_count === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-3">All Caught Up!</h2>
          <p className="text-slate-500 mb-8">
            No cards due for review. Great job staying on top of your learning!
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate('/study', { state: { dictionaryType, difficultOnly: true } })}
              className="px-6 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors"
            >
              Review difficult words
            </button>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] overflow-y-auto overflow-x-hidden bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex flex-col">
      {/* Top Bar */}
      <div className="flex-shrink-0 px-4 py-2 flex items-center justify-between max-w-5xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors bg-white/60 backdrop-blur-sm px-3 py-2 rounded-xl border border-slate-200 hover:border-indigo-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-3">
          {hskLevel && (
            <span className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md">
              HSK {hskLevel}
            </span>
          )}
          {!hskLevel && (
            <span className="bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl text-sm font-medium text-slate-500 border border-slate-200">
              {dictionaryType === 'ielts' ? 'IELTS English' : 'All HSK Levels'}
            </span>
          )}
          <button
            onClick={() => setShuffle(!shuffle)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all border ${
              shuffle
                ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200'
                : 'bg-white/60 text-slate-500 border-slate-200 hover:border-indigo-200 hover:text-indigo-600'
            } backdrop-blur-sm`}
            title={shuffle ? 'Shuffle ON - Cards randomized' : 'Shuffle OFF - Cards in order'}
          >
            <Shuffle className="w-4 h-4" />
            <span>{shuffle ? 'On' : 'Off'}</span>
          </button>
        </div>
        <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-slate-200">
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold text-indigo-600">
              {dailyReviewed} / {dailyGoal}
            </div>
            <div className="text-[10px] text-slate-500">Today</div>
          </div>
          <div className="h-7 w-px bg-slate-200" />
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold text-slate-700">
              {currentIndex + 1} / {sessionCardCount}
            </div>
            <div className="text-[10px] text-slate-500">Session</div>
          </div>
          <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden ml-2">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-400 to-violet-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Card Area */}
      <div className="flex-none flex items-start justify-center px-3 pb-4 pt-1">
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-4 items-start">
          <div className="min-w-0">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              initial={{ opacity: 0, x: direction * 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * 50 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {/* Main Card - Horizontal Layout */}
              <div
                onClick={() => {
                  if (!revealed) {
                    setRevealed(true);
                    speakCard(currentCard?.word);
                  }
                }}
                className="bg-white rounded-3xl shadow-lg shadow-indigo-100/50 border-2 border-indigo-100 overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-indigo-200/50 hover:border-indigo-200 transition-all duration-300"
              >
                <div className="flex flex-col items-stretch min-h-[260px]">
                  {/* Left Side - Word Display (Always Visible) */}
                  <div
                    className="w-full flex flex-col items-center justify-center px-4 md:px-6 py-5 bg-gradient-to-b from-white to-indigo-50/30 border-b-2 border-indigo-100"
                  >
                    <motion.h1
                      className="text-6xl font-bold bg-gradient-to-br from-slate-800 to-indigo-900 bg-clip-text text-transparent mb-3 leading-tight"
                      initial={{ scale: 0.95 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {currentCard?.word}
                    </motion.h1>

                    <div className="flex items-center justify-center gap-2 mb-1">
                      <p className="text-xl text-slate-400 font-light">{currentCard?.pinyin}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          speakCard(currentCard?.word);
                        }}
                        className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        title="Listen to pronunciation"
                      >
                        <Volume2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-2 mt-3">
                      {currentCard?.hsk_level && (
                        <span className="px-4 py-1.5 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-600 rounded-full text-xs font-semibold border border-indigo-200">
                          HSK {currentCard.hsk_level}
                        </span>
                      )}
                      {currentCard?.part_of_speech && (
                        <span className="px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
                          {currentCard.part_of_speech}
                        </span>
                      )}
                      {currentCard?.tags?.map((tag) => (
                        <span key={tag} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold border border-slate-200">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Hint when not revealed */}
                    {!revealed && (
                      <p className="text-sm text-indigo-400 font-medium mt-6">
                        Click to reveal
                      </p>
                    )}
                    {/* Flagged indicator */}
                    {currentCard?.flagged && (
                      <div className="mt-4 flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-200 rounded-full">
                        <Flag className="w-3.5 h-3.5 text-amber-600 fill-amber-600" />
                        <span className="text-xs font-semibold text-amber-700">Flagged</span>
                      </div>
                    )}                  </div>

                  {/* Reveal Section (Expands Down) */}
                  <AnimatePresence>
                    {revealed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0, y: -10 }}
                        animate={{ height: "auto", opacity: 1, y: 0 }}
                        exit={{ height: 0, opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full overflow-hidden bg-gradient-to-b from-indigo-50/50 to-white"
                      >
                        <div className="w-full min-w-0 px-4 md:px-6 py-4 flex flex-col justify-center">
                          {/* Meanings */}
                          <div className="space-y-2 mb-4">
                            {currentCard?.meaning_en && (
                              <div className="flex items-start gap-3 bg-blue-50 rounded-xl px-3 py-2 border border-blue-100">
                                <span className="text-xs font-bold text-blue-500 mt-0.5 px-1.5 py-0.5 bg-blue-100 rounded">EN</span>
                                <p className="text-base text-blue-800 font-medium">{currentCard.meaning_en}</p>
                              </div>
                            )}
                            {currentCard?.meaning_vi && (
                              <div className="flex items-start gap-3 bg-emerald-50 rounded-xl px-3 py-2 border border-emerald-100">
                                <span className="text-xs font-bold text-emerald-500 mt-0.5 px-1.5 py-0.5 bg-emerald-100 rounded">VI</span>
                                <p className="text-base text-emerald-800 font-medium">{currentCard.meaning_vi}</p>
                              </div>
                            )}
                          </div>

                          {/* Example Sentence */}
                          {currentCard?.example_sentence && (
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-3 space-y-1.5 border border-amber-200">
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-lg md:text-xl text-slate-800 font-semibold leading-relaxed">{currentCard.example_sentence}</p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    speakText(currentCard.example_sentence, dictionaryType === 'ielts' ? 'en-US' : 'zh-CN');
                                  }}
                                  className="flex-shrink-0 p-2 text-orange-500 hover:text-orange-700 hover:bg-orange-100 rounded-lg transition-colors"
                                  title="Listen to example sentence"
                                  aria-label="Listen to example sentence"
                                >
                                  <Volume2 className="w-5 h-5" />
                                </button>
                              </div>
                              {currentCard?.example_sentence_pinyin && (
                                <p className="text-sm text-indigo-600 font-medium">{currentCard.example_sentence_pinyin}</p>
                              )}
                              {currentCard?.example_translation_vi && (
                                <p className="text-sm text-emerald-600 font-medium">{currentCard.example_translation_vi}</p>
                              )}
                            </div>
                          )}

                          {/* Rating Buttons */}
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReview(0);
                              }}
                              disabled={reviewing}
                              className="py-2.5 px-3 bg-gradient-to-b from-rose-50 to-rose-100 hover:from-rose-100 hover:to-rose-200 text-rose-600 rounded-xl font-semibold transition-all text-sm border-2 border-rose-200 hover:border-rose-300 shadow-sm hover:shadow active:scale-95"
                            >
                              Again
                              <span className="block text-xs opacity-60 mt-0.5">1</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReview(1);
                              }}
                              disabled={reviewing}
                              className="py-2.5 px-3 bg-gradient-to-b from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 text-amber-600 rounded-xl font-semibold transition-all text-sm border-2 border-amber-200 hover:border-amber-300 shadow-sm hover:shadow active:scale-95"
                            >
                              Hard
                              <span className="block text-xs opacity-60 mt-0.5">2</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReview(2);
                              }}
                              disabled={reviewing}
                              className="py-2.5 px-3 bg-gradient-to-b from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 text-blue-600 rounded-xl font-semibold transition-all text-sm border-2 border-blue-200 hover:border-blue-300 shadow-sm hover:shadow active:scale-95"
                            >
                              Good
                              <span className="block text-xs opacity-60 mt-0.5">3</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReview(3);
                              }}
                              disabled={reviewing}
                              className="py-2.5 px-3 bg-gradient-to-b from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 text-emerald-600 rounded-xl font-semibold transition-all text-sm border-2 border-emerald-200 hover:border-emerald-300 shadow-sm hover:shadow active:scale-95"
                            >
                              Easy
                              <span className="block text-xs opacity-60 mt-0.5">4</span>
                            </button>
                          </div>

                          {/* Flag Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFlagComment(currentCard?.user_comment || '');
                              setShowFlagModal(true);
                            }}
                            disabled={flagging}
                            className={`mt-3 w-full py-2.5 px-4 rounded-xl font-semibold transition-all text-sm border-2 flex items-center justify-center gap-2 ${
                              currentCard?.flagged
                                ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200'
                                : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:border-slate-300'
                            }`}
                          >
                            <Flag className={`w-4 h-4 ${currentCard?.flagged ? 'fill-amber-600' : ''}`} />
                            {currentCard?.flagged ? 'Remove Flag' : 'Flag Incorrect Meaning'}
                          </button>
                        </div>
                      </motion.div>
                  )}
                </AnimatePresence>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0 || reviewing}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white/70 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="View previous card"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-xs text-slate-400">
                  {isCurrentCompleted ? 'Reviewed · re-rate to update' : 'Review once, then continue'}
                </span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!session || currentIndex >= session.cards.length - 1 || reviewing}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white/70 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="View next card"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Keyboard Hints */}
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-400 font-medium">
                  {revealed ? (
                    <>
                      <kbd className="px-2 py-0.5 bg-white/60 rounded-md border border-slate-200">←</kbd> Again
                      {' · '}
                      <kbd className="px-2 py-0.5 bg-white/60 rounded-md border border-slate-200">→</kbd> Good
                      {' · '}
                      <kbd className="px-2 py-0.5 bg-white/60 rounded-md border border-slate-200">1-4</kbd> Rate
                    </>
                  ) : (
                    <>
                      <kbd className="px-2 py-0.5 bg-white/60 rounded-md border border-slate-200">Space</kbd> or click to reveal
                    </>
                  )}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>

          {motivationQuote && (
            <aside className="w-full lg:sticky lg:top-6 bg-white/75 backdrop-blur-sm rounded-2xl border border-amber-200 shadow-lg shadow-amber-100/50 p-5">
              <div className="flex items-center gap-2 text-amber-600 mb-4">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Study Buddy</span>
              </div>
              <blockquote className="text-lg font-semibold leading-relaxed text-slate-800">
                “{motivationQuote.message}”
              </blockquote>
              {motivationQuote.pinyin && (
                <p className="mt-3 text-sm leading-relaxed text-indigo-600">{motivationQuote.pinyin}</p>
              )}
              <p className="mt-3 text-sm leading-relaxed text-emerald-700">{motivationQuote.meaning_vi}</p>
              <div className="mt-5 flex items-center justify-between border-t border-amber-100 pt-3">
                <span className="text-xs font-medium text-slate-400">A little nudge</span>
                <span className="text-xs font-semibold text-amber-500">#{motivationQuote.category}</span>
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Flag Modal */}
      <AnimatePresence>
        {showFlagModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowFlagModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-slate-200"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-amber-500 fill-amber-500" />
                  {currentCard?.flagged ? 'Remove Flag' : 'Flag Incorrect Meaning'}
                </h3>
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {currentCard && (
                <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-3xl font-bold text-slate-800 mb-1">{currentCard.word}</p>
                  <p className="text-sm text-slate-500">{currentCard.pinyin}</p>
                  {currentCard.meaning_en && (
                    <p className="text-sm text-blue-600 mt-2">{currentCard.meaning_en}</p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4" />
                  Comment (optional)
                </label>
                <textarea
                  value={flagComment}
                  onChange={(e) => setFlagComment(e.target.value)}
                  placeholder="Why is this meaning incorrect? What's the correct meaning?"
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none resize-none text-sm"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="flex-1 py-2.5 px-4 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors text-sm"
                >
                  Cancel
                </button>
                {currentCard?.flagged ? (
                  <>
                    <button
                      onClick={handleUpdateFlag}
                      disabled={flagging}
                      className="flex-1 py-2.5 px-4 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors text-sm"
                    >
                      Update
                    </button>
                    <button
                      onClick={handleFlag}
                      disabled={flagging}
                      className="flex-1 py-2.5 px-4 bg-slate-600 text-white rounded-xl font-medium hover:bg-slate-700 transition-colors text-sm"
                    >
                      Remove Flag
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleFlag}
                    disabled={flagging}
                    className="flex-1 py-2.5 px-4 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors text-sm"
                  >
                    Flag This Card
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudySession;
